/**
 * BrandingSettings Component
 * White-label customization settings
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../hooks/useTenant';

const BrandingSettings: React.FC = () => {
  const navigate = useNavigate();
  const { tenant, branding, updateBranding, hasFeature } = useTenant();
  const [formData, setFormData] = useState(branding);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const isEnterprise = tenant?.tier === 'enterprise';
  const canWhiteLabel = hasFeature('white_label');

  useEffect(() => {
    setFormData(branding);
  }, [branding]);

  const handleChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleUpgrade = () => {
    navigate('/settings/billing');
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      await updateBranding(formData);
      setMessage('Branding updated successfully!');
    } catch (error) {
      setMessage('Failed to update branding. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const colorFields = [
    { key: 'primary_color', label: 'Primary Color', desc: 'Main brand color for buttons and highlights' },
    { key: 'secondary_color', label: 'Secondary Color', desc: 'Supporting color for UI elements' },
    { key: 'accent_color', label: 'Accent Color', desc: 'Accent for success states and highlights' },
    { key: 'danger_color', label: 'Danger Color', desc: 'Error and warning states' },
    { key: 'background_primary', label: 'Background Primary', desc: 'Main background color' },
    { key: 'background_secondary', label: 'Background Secondary', desc: 'Card and panel backgrounds' },
    { key: 'text_primary', label: 'Text Primary', desc: 'Main text color' },
    { key: 'text_secondary', label: 'Text Secondary', desc: 'Secondary and muted text' },
  ];

  return (
    <div className="branding-settings">
      <div className="settings-header">
        <div>
          <h2>🎨 Branding Settings</h2>
          <p>Customize your platform's appearance</p>
        </div>
        <div className="tier-badge">
          {tenant?.tier?.toUpperCase() || 'STARTER'}
        </div>
      </div>

      {/* Tier Notice */}
      {!canWhiteLabel && (
        <div className="upgrade-notice">
          <div className="notice-icon">✨</div>
          <div className="notice-content">
            <strong>Upgrade to Enterprise for Full White-Label</strong>
            <p>Remove "Powered by ShopDrawings.AI" branding and unlock all customization options.</p>
          </div>
          <button className="btn primary" onClick={handleUpgrade}>Upgrade Now</button>
        </div>
      )}

      {/* Message */}
      {message && (
        <div className={`message ${message.includes('success') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      {/* Settings Form */}
      <div className="settings-grid">
        {/* Logo Section */}
        <div className="settings-card">
          <h3>Logo</h3>
          <div className="form-group">
            <label>Logo URL (Light Mode)</label>
            <input
              type="text"
              value={formData.logo_url || ''}
              onChange={(e) => handleChange('logo_url', e.target.value)}
              placeholder="https://yoursite.com/logo.svg"
            />
          </div>
          <div className="form-group">
            <label>Logo URL (Dark Mode)</label>
            <input
              type="text"
              value={formData.logo_dark_url || ''}
              onChange={(e) => handleChange('logo_dark_url', e.target.value)}
              placeholder="https://yoursite.com/logo-dark.svg"
            />
          </div>
          <div className="form-group">
            <label>Favicon URL</label>
            <input
              type="text"
              value={formData.favicon_url || ''}
              onChange={(e) => handleChange('favicon_url', e.target.value)}
              placeholder="https://yoursite.com/favicon.ico"
            />
          </div>
          <div className="preview-box">
            <label>Preview</label>
            <div className="logo-preview">
              <img 
                src={formData.logo_url} 
                alt="Logo Preview"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/static/logo-default.svg';
                }}
              />
            </div>
          </div>
        </div>

        {/* Company Info */}
        <div className="settings-card">
          <h3>Company Info</h3>
          <div className="form-group">
            <label>Company Name</label>
            <input
              type="text"
              value={formData.company_name || ''}
              onChange={(e) => handleChange('company_name', e.target.value)}
              placeholder="Your Company Name"
            />
          </div>
          <div className="form-group">
            <label>Tagline</label>
            <input
              type="text"
              value={formData.tagline || ''}
              onChange={(e) => handleChange('tagline', e.target.value)}
              placeholder="Professional Shop Drawings"
            />
          </div>
          <div className="form-group">
            <label>Support Email</label>
            <input
              type="email"
              value={formData.support_email || ''}
              onChange={(e) => handleChange('support_email', e.target.value)}
              placeholder="support@yourcompany.com"
            />
          </div>
          <div className="form-group">
            <label>Footer Text</label>
            <input
              type="text"
              value={formData.footer_text || ''}
              onChange={(e) => handleChange('footer_text', e.target.value)}
              placeholder="© 2024 Your Company. All rights reserved."
            />
          </div>
        </div>

        {/* Colors */}
        <div className="settings-card full-width">
          <h3>Brand Colors</h3>
          <div className="colors-grid">
            {colorFields.map(({ key, label, desc }) => (
              <div key={key} className="color-field">
                <div className="color-input-group">
                  <input
                    type="color"
                    value={(formData as unknown as Record<string, string>)[key] || '#000000'}
                    onChange={(e) => handleChange(key, e.target.value)}
                    className="color-picker"
                  />
                  <div className="color-info">
                    <label>{label}</label>
                    <span className="color-desc">{desc}</span>
                  </div>
                </div>
                <input
                  type="text"
                  value={(formData as unknown as Record<string, string>)[key] || ''}
                  onChange={(e) => handleChange(key, e.target.value)}
                  className="color-hex"
                  placeholder="#000000"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Typography */}
        <div className="settings-card">
          <h3>Typography</h3>
          <div className="form-group">
            <label>Primary Font Family</label>
            <select
              value={formData.font_family || 'Inter, sans-serif'}
              onChange={(e) => handleChange('font_family', e.target.value)}
            >
              <option value="Inter, sans-serif">Inter</option>
              <option value="'Roboto', sans-serif">Roboto</option>
              <option value="'Open Sans', sans-serif">Open Sans</option>
              <option value="'Lato', sans-serif">Lato</option>
              <option value="'Poppins', sans-serif">Poppins</option>
              <option value="'Nunito', sans-serif">Nunito</option>
              <option value="system-ui, sans-serif">System Default</option>
            </select>
          </div>
          <div className="form-group">
            <label>Heading Font Family</label>
            <select
              value={formData.font_heading || 'Inter, sans-serif'}
              onChange={(e) => handleChange('font_heading', e.target.value)}
            >
              <option value="Inter, sans-serif">Inter</option>
              <option value="'Roboto', sans-serif">Roboto</option>
              <option value="'Open Sans', sans-serif">Open Sans</option>
              <option value="'Montserrat', sans-serif">Montserrat</option>
              <option value="'Poppins', sans-serif">Poppins</option>
            </select>
          </div>
          <div className="form-group">
            <label>Border Radius</label>
            <select
              value={formData.border_radius || '8px'}
              onChange={(e) => handleChange('border_radius', e.target.value)}
            >
              <option value="0px">None (0px)</option>
              <option value="4px">Small (4px)</option>
              <option value="8px">Medium (8px)</option>
              <option value="12px">Large (12px)</option>
              <option value="16px">Extra Large (16px)</option>
            </select>
          </div>
        </div>

        {/* White Label */}
        <div className="settings-card">
          <h3>White Label</h3>
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={!formData.show_powered_by}
                onChange={(e) => handleChange('show_powered_by', !e.target.checked)}
                disabled={!canWhiteLabel}
              />
              <span>Hide "Powered by ShopDrawings.AI"</span>
            </label>
            {!canWhiteLabel && (
              <span className="enterprise-badge">Enterprise Only</span>
            )}
          </div>
          <div className="white-label-preview">
            {formData.show_powered_by ? (
              <div className="preview-footer">
                Powered by <a href="https://shopdrawings.ai" target="_blank" rel="noopener noreferrer">ShopDrawings.AI</a>
              </div>
            ) : (
              <div className="preview-footer clean">
                {formData.footer_text || `© ${new Date().getFullYear()} ${formData.company_name}`}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Live Preview */}
      <div className="preview-section">
        <h3>Live Preview</h3>
        <div 
          className="preview-container"
          style={{
            '--preview-primary': formData.primary_color,
            '--preview-secondary': formData.secondary_color,
            '--preview-bg': formData.background_primary,
            '--preview-bg-secondary': formData.background_secondary,
            '--preview-text': formData.text_primary,
            '--preview-text-secondary': formData.text_secondary,
            '--preview-radius': formData.border_radius,
            fontFamily: formData.font_family,
          } as React.CSSProperties}
        >
          <div className="preview-header">
            <img src={formData.logo_url} alt="Logo" className="preview-logo" />
            <span style={{ fontFamily: formData.font_heading }}>{formData.company_name}</span>
          </div>
          <div className="preview-content">
            <button className="preview-btn primary">Primary Button</button>
            <button className="preview-btn secondary">Secondary</button>
          </div>
          <div className="preview-card">
            <h4>Sample Card</h4>
            <p>This is how your cards will look.</p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="settings-footer">
        <button className="btn secondary" onClick={() => setFormData(branding)}>
          Reset Changes
        </button>
        <button className="btn primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <style>{`
        .branding-settings {
          padding: 0;
        }

        .settings-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
        }

        .settings-header h2 {
          margin: 0 0 4px;
        }

        .settings-header p {
          margin: 0;
          color: var(--text-secondary);
        }

        .tier-badge {
          padding: 4px 12px;
          background: var(--primary);
          color: white;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }

        .upgrade-notice {
          display: flex;
          align-items: center;
          gap: 16px;
          background: linear-gradient(135deg, rgba(37, 99, 235, 0.1), rgba(139, 92, 246, 0.1));
          border: 1px solid var(--primary);
          border-radius: 12px;
          padding: 16px 20px;
          margin-bottom: 24px;
        }

        .notice-icon {
          font-size: 24px;
        }

        .notice-content {
          flex: 1;
        }

        .notice-content p {
          margin: 4px 0 0;
          color: var(--text-secondary);
          font-size: 14px;
        }

        .message {
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 24px;
        }

        .message.success {
          background: rgba(16, 185, 129, 0.1);
          color: var(--success);
          border: 1px solid var(--success);
        }

        .message.error {
          background: rgba(239, 68, 68, 0.1);
          color: var(--danger);
          border: 1px solid var(--danger);
        }

        .settings-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 24px;
          margin-bottom: 24px;
        }

        .settings-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color, #334155);
          border-radius: 12px;
          padding: 24px;
        }

        .settings-card.full-width {
          grid-column: span 2;
        }

        .settings-card h3 {
          margin: 0 0 20px;
          font-size: 16px;
        }

        .form-group {
          margin-bottom: 16px;
        }

        .form-group label {
          display: block;
          font-size: 14px;
          color: var(--text-secondary);
          margin-bottom: 6px;
        }

        .form-group input,
        .form-group select {
          width: 100%;
          padding: 10px 14px;
          background: var(--bg-primary);
          border: 1px solid var(--border-color, #334155);
          border-radius: 8px;
          color: var(--text-primary);
          font-size: 14px;
        }

        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: var(--primary);
        }

        .preview-box {
          margin-top: 20px;
          padding-top: 16px;
          border-top: 1px solid var(--border-color, #334155);
        }

        .logo-preview {
          background: var(--bg-primary);
          padding: 20px;
          border-radius: 8px;
          text-align: center;
        }

        .logo-preview img {
          max-height: 48px;
          max-width: 100%;
        }

        .colors-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .color-field {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .color-input-group {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .color-picker {
          width: 48px;
          height: 48px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          padding: 0;
        }

        .color-info {
          flex: 1;
        }

        .color-info label {
          display: block;
          font-weight: 500;
          margin-bottom: 2px;
        }

        .color-desc {
          font-size: 12px;
          color: var(--text-secondary);
        }

        .color-hex {
          padding: 8px 12px;
          background: var(--bg-primary);
          border: 1px solid var(--border-color, #334155);
          border-radius: 6px;
          color: var(--text-primary);
          font-family: monospace;
          font-size: 13px;
        }

        .checkbox-group {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }

        .checkbox-label input {
          width: 18px;
          height: 18px;
          accent-color: var(--primary);
        }

        .enterprise-badge {
          font-size: 11px;
          padding: 2px 8px;
          background: rgba(139, 92, 246, 0.2);
          color: #a78bfa;
          border-radius: 10px;
        }

        .white-label-preview {
          margin-top: 16px;
          padding: 16px;
          background: var(--bg-primary);
          border-radius: 8px;
          text-align: center;
        }

        .preview-footer {
          font-size: 12px;
          color: var(--text-secondary);
        }

        .preview-footer a {
          color: var(--primary);
          text-decoration: none;
        }

        .preview-section {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color, #334155);
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 24px;
        }

        .preview-section h3 {
          margin: 0 0 16px;
        }

        .preview-container {
          background: var(--preview-bg);
          border-radius: var(--preview-radius);
          padding: 24px;
        }

        .preview-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
          color: var(--preview-text);
          font-weight: 600;
        }

        .preview-logo {
          height: 32px;
        }

        .preview-content {
          display: flex;
          gap: 12px;
          margin-bottom: 20px;
        }

        .preview-btn {
          padding: 10px 20px;
          border: none;
          border-radius: var(--preview-radius);
          cursor: pointer;
          font-size: 14px;
        }

        .preview-btn.primary {
          background: var(--preview-primary);
          color: white;
        }

        .preview-btn.secondary {
          background: var(--preview-bg-secondary);
          color: var(--preview-text);
          border: 1px solid var(--border-color, #334155);
        }

        .preview-card {
          background: var(--preview-bg-secondary);
          padding: 20px;
          border-radius: var(--preview-radius);
        }

        .preview-card h4 {
          margin: 0 0 8px;
          color: var(--preview-text);
        }

        .preview-card p {
          margin: 0;
          color: var(--preview-text-secondary);
        }

        .settings-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }

        @media (max-width: 768px) {
          .settings-grid {
            grid-template-columns: 1fr;
          }

          .settings-card.full-width {
            grid-column: span 1;
          }

          .colors-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default BrandingSettings;
